/**
 * Shared display helpers.
 *
 * BALANCE
 *   The profile API returns `coins` in minor units: the user's balance is coins / 100.
 *   (Content.vue already did this; MainBalance.vue and WithdrawFund.vue did not, which is
 *   why the same account showed different numbers on different screens.)
 *
 * NAMES
 *   The API can return the STRING "null" for a missing middle name, and bakes it into
 *   full_name too (e.g. "Sushil null Subedi"). These helpers strip it.
 */

export const COINS_PER_UNIT = 100;

/** coins (minor units) -> account balance as a Number */
export function coinsToBalance(coins) {
  const n = Number(coins);
  if (isNaN(n)) { return 0; }
  return n / COINS_PER_UNIT;
}

/** Any numeric value -> fixed 2-decimal string */
export function formatMoney(value, decimals) {
  const n = Number(value);
  const d = (decimals === undefined || decimals === null) ? 2 : decimals;
  return (isNaN(n) ? 0 : n).toFixed(d);
}

/**
 * MINOR UNITS
 *
 * The API talks in cents everywhere money is a plain number - wallet balance, withdrawal
 * amounts, fees, and the min/max values inside withdraw_limits. `POST /v1/merchant/withdraws/quote`
 * states it outright ("POST an amount in cents") and its own payload proves it:
 *   amount 111  -> display "$1.11"
 *   balance 69381 -> $693.81
 *   slab min_amount 2500 -> label "$25.0"
 * Reading a cents value as if it were dollars is what made the merchant withdraw page
 * show 69576.00 for a balance of 695.76.
 *
 * Use centsToAmount() on everything coming FROM the API and amountToCents() on everything
 * going TO it. Never do the arithmetic inline - a stray /100 in one component and not the
 * next is exactly how the two screens disagreed in the first place.
 */

/** cents -> amount as a Number:  69381 -> 693.81 */
export function centsToAmount(cents) {
  const n = Number(cents);
  if (isNaN(n)) { return 0; }
  return n / COINS_PER_UNIT;
}

/** amount -> cents, rounded so 1.115 * 100 never lands on 111.49999999999999 */
export function amountToCents(amount) {
  const n = Number(amount);
  if (isNaN(n)) { return 0; }
  return Math.round(n * COINS_PER_UNIT);
}

/** cents -> "693.81" */
export function formatCents(cents, decimals) {
  return formatMoney(centsToAmount(cents), decimals);
}

/** coins -> "947.11" */
export function formatBalance(coins, decimals) {
  return formatMoney(coinsToBalance(coins), decimals);
}

/** True for null, undefined, "", "null", "undefined", "none" (case-insensitive). */
export function isBlankish(value) {
  if (value === null || value === undefined) { return true; }
  const s = String(value).trim().toLowerCase();
  return s === "" || s === "null" || s === "undefined" || s === "none";
}

/** "Sushil null Subedi" -> "Sushil Subedi";  "null" -> "" */
export function cleanName(value) {
  if (isBlankish(value)) { return ""; }
  return String(value)
    .split(/\s+/)
    .filter(function (part) { return !isBlankish(part); })
    .join(" ")
    .trim();
}

/** Build a display name from parts, skipping blank/"null" pieces. */
export function buildFullName(user) {
  if (!user) { return ""; }
  const parts = [user.first_name, user.middle_name, user.last_name]
    .filter(function (p) { return !isBlankish(p); });
  if (parts.length) { return parts.join(" "); }
  return cleanName(user.full_name);
}


/**
 * Strips a standalone "null" word from backend-generated sentences such as
 * "Dear Sushil null Subedi Your AmejPay Account has been Debited by 222.0".
 * Only whole words are removed, so text like "nullify" is untouched.
 */
export function cleanText(value) {
  if (value === null || value === undefined) { return ""; }
  return String(value)
    .replace(/\b(?:null|undefined)\b/gi, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

const NAME_KEYS = ["full_name", "middle_name", "first_name", "last_name",
                   "account_holder_name", "owner_full_name", "contact_person_name"];

/**
 * Recursively clean name fields on anything coming back from the API, so every screen
 * shows the same corrected value without touching 50+ templates.
 */
export function sanitizeNames(data, depth) {
  const d = depth || 0;
  if (d > 6 || data === null || data === undefined) { return data; }
  if (Array.isArray(data)) {
    for (let i = 0; i < data.length; i++) { sanitizeNames(data[i], d + 1); }
    return data;
  }
  if (typeof data !== "object") { return data; }
  for (let i = 0; i < NAME_KEYS.length; i++) {
    const key = NAME_KEYS[i];
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      const cleaned = cleanName(data[key]);
      data[key] = cleaned;
    }
  }
  // If full_name was blank or only contained "null", rebuild it from the parts.
  if (Object.prototype.hasOwnProperty.call(data, "full_name") && !data.full_name) {
    const rebuilt = buildFullName(data);
    if (rebuilt) { data.full_name = rebuilt; }
  }
  const keys = Object.keys(data);
  for (let i = 0; i < keys.length; i++) {
    const v = data[keys[i]];
    if (v && typeof v === "object") { sanitizeNames(v, d + 1); }
  }
  return data;
}


/* ------------------------------------------------------------------ *
 * PHONE
 *
 * GET /v1/profile returns the number as a LIST on the author:
 *   author.authorphone: [{ phone: "9876540003", country_code: "+977" }]
 *
 * Several things about that list bite in practice, and each one shows up on screen
 * as the same unhelpful "Not added":
 *   - `profile` starts life as [] and one legacy action pushed into it, so the object
 *     is sometimes wrapped in an array
 *   - the first entry can be a blank placeholder while a later one holds the number
 *   - country_code arrives with or without the leading "+"
 *   - older payloads used phone_number / number / phone_country_code
 * resolvePhone handles all of them and returns "" only when there is genuinely no
 * number anywhere.
 * ------------------------------------------------------------------ */

/** Unwraps a profile that may have been stored as [profile] instead of profile. */
export function unwrapProfile(profile) {
  if (Array.isArray(profile)) { return profile.length ? profile[0] : {}; }
  return profile || {};
}

/** "+977 9876540003" from any entry that actually carries a number, else "". */
export function resolvePhone(profile) {
  const p = unwrapProfile(profile);
  const author = p.author || p;

  const lists = [author.authorphone, p.authorphone, author.phones, author.phone_numbers];
  for (let i = 0; i < lists.length; i++) {
    const list = lists[i];
    if (!Array.isArray(list)) { continue; }
    for (let j = 0; j < list.length; j++) {
      const entry = list[j] || {};
      const num = entry.phone || entry.phone_number || entry.number || '';
      if (!String(num).trim()) { continue; }
      const code = entry.country_code || entry.phone_country_code || entry.dialing_code || '';
      return formatPhone(code, num);
    }
  }

  // Flat fields, for payloads that never used the list form.
  const flat = author.phone || author.phone_number || '';
  if (String(flat).trim()) {
    return formatPhone(author.phone_country_code || author.country_code || '', flat);
  }

  return '';
}

function formatPhone(code, number) {
  const num = String(number).trim();
  const c = String(code || '').trim();
  if (!c) { return num; }
  return (c.charAt(0) === '+' ? c : '+' + c) + ' ' + num;
}

export default {
  COINS_PER_UNIT, coinsToBalance, centsToAmount, amountToCents, formatCents,
  formatMoney, formatBalance,
  isBlankish, cleanName, cleanText, buildFullName, sanitizeNames,
  unwrapProfile, resolvePhone,
};
